import { startTransition, useEffect, useState } from 'react'
import { Mail, Menu, Phone, Truck, X } from 'lucide-react'
import heroImage from '../images/main_photo.png'

const initialFormState = {
  name: '',
  contact: '',
  model: '',
  quantity: '1',
  message: '',
}

const galleryImages = {
  hero: heroImage,
  social: '/clusterball-table-social.png',
}

const gameDemoVideoSrc = '/clusterballVid.MP4'

function getCsrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

function validateForm(formState) {
  const nextErrors = {}

  if (!formState.name.trim()) {
    nextErrors.name = 'Укажите имя.'
  }

  if (!formState.contact.trim()) {
    nextErrors.contact = 'Оставьте телефон или Telegram.'
  }

  if (!formState.model.trim()) {
    nextErrors.model = 'Выберите комплектацию.'
  }

  if (!formState.quantity.trim()) {
    nextErrors.quantity = 'Укажите количество.'
  } else if (!/^\d+$/.test(formState.quantity.trim()) || Number(formState.quantity.trim()) < 1) {
    nextErrors.quantity = 'Количество должно быть целым числом от 1.'
  }

  if (formState.message.length > 1000) {
    nextErrors.message = 'Комментарий не должен быть длиннее 1000 символов.'
  }

  return nextErrors
}

function buildLeadMessage(formState) {
  const messageParts = [
    `Комплектация: ${formState.model.trim()}`,
    `Количество: ${formState.quantity.trim()}`,
  ]

  if (formState.message.trim()) {
    messageParts.push(`Комментарий: ${formState.message.trim()}`)
  }

  return messageParts.join('\n')
}

const contactIcons = {
  truck: Truck,
  phone: Phone,
  mail: Mail,
}

function LeadForm({ formConfig, purchaseOptions, preselectedModel, telegramReady, telegramUrl }) {
  const [formState, setFormState] = useState(initialFormState)
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [feedback, setFeedback] = useState({ tone: 'idle', message: '' })

  useEffect(() => {
    if (!preselectedModel) return

    setFormState((current) => ({
      ...current,
      model: preselectedModel,
      quantity: current.quantity.trim() ? current.quantity : '1',
    }))

    setErrors((current) => {
      const nextErrors = { ...current }
      delete nextErrors.model
      return nextErrors
    })
  }, [preselectedModel])

  function updateField(event) {
    const { name, value } = event.target

    setFormState((current) => ({
      ...current,
      [name]: value,
    }))

    if (errors[name]) {
      setErrors((current) => {
        const nextErrors = { ...current }
        delete nextErrors[name]
        return nextErrors
      })
    }
  }

  async function handleSubmit(event) {
    event.preventDefault()

    const nextErrors = validateForm(formState)
    setErrors(nextErrors)

    if (Object.keys(nextErrors).length > 0) {
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/leads', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          lead: {
            name: formState.name,
            contact: formState.contact,
            message: buildLeadMessage(formState),
          },
        }),
      })

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        setErrors(payload.errors || {})
        startTransition(() => {
          setFeedback({
            tone: 'error',
            message: payload.message || 'Не удалось отправить заявку. Попробуйте ещё раз.',
          })
        })
        return
      }

      setFormState(initialFormState)
      startTransition(() => {
        setFeedback({
          tone: response.status === 202 ? 'warning' : 'success',
          message: payload.message || 'Спасибо. Заявка отправлена.',
        })
      })
    } catch {
      startTransition(() => {
        setFeedback({
          tone: 'error',
          message: 'Не удалось отправить заявку. Проверьте соединение и попробуйте ещё раз.',
        })
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="contact-panel" id="lead-form">
      <div className="contact-copy">
        <span className="section-kicker">Покупка</span>
        <h2>{formConfig.title}</h2>
        <p>{formConfig.text}</p>
        {formConfig.contacts?.length ? (
          <div className="contact-details">
            {formConfig.contacts.map((item) => {
              const Icon = contactIcons[item.icon] || Truck

              return item.href ? (
                <a className="contact-detail-link" href={item.href} key={`${item.label}-${item.href}`}>
                  <span className="contact-detail-icon" aria-hidden="true">
                    <Icon size={16} strokeWidth={1.8} />
                  </span>
                  <span>{item.label}</span>
                </a>
              ) : (
                <p className="contact-detail-text" key={item.label}>
                  <span className="contact-detail-icon" aria-hidden="true">
                    <Icon size={16} strokeWidth={1.8} />
                  </span>
                  <span>{item.label}</span>
                </p>
              )
            })}
          </div>
        ) : null}
        {telegramReady ? (
          <a className="telegram-inline" href={telegramUrl} rel="noreferrer" target="_blank">
            Открыть Telegram
          </a>
        ) : null}
        {formConfig.note ? <p className="contact-note">{formConfig.note}</p> : null}
      </div>

      <form className="lead-form" onSubmit={handleSubmit}>
        <label className="field">
          <span>Имя</span>
          <input
            autoComplete="name"
            name="name"
            onChange={updateField}
            placeholder="Ваше имя"
            value={formState.name}
          />
          {errors.name ? <small>{errors.name}</small> : null}
        </label>

        <label className="field">
          <span>Телефон или Telegram</span>
          <input
            autoComplete="tel"
            name="contact"
            onChange={updateField}
            placeholder="+7 или @username"
            value={formState.contact}
          />
          {errors.contact ? <small>{errors.contact}</small> : null}
        </label>

        <label className="field">
          <span>Комплектация</span>
          <select name="model" onChange={updateField} value={formState.model}>
            <option value="">Выберите комплектацию</option>
            {purchaseOptions.map((item) => (
              <option key={item.title} value={item.title}>
                {item.title}
              </option>
            ))}
          </select>
          {errors.model ? <small>{errors.model}</small> : null}
        </label>

        <label className="field">
          <span>Количество</span>
          <input
            inputMode="numeric"
            name="quantity"
            onChange={updateField}
            placeholder="1"
            value={formState.quantity}
          />
          {errors.quantity ? <small>{errors.quantity}</small> : null}
        </label>

        <label className="field">
          <span>Комментарий</span>
          <textarea
            name="message"
            onChange={updateField}
            placeholder="Комментарий по покупке, доставке или срокам"
            rows="5"
            value={formState.message}
          />
          {errors.message ? <small>{errors.message}</small> : null}
        </label>

        <button className="primary-button" disabled={isSubmitting} type="submit">
          {isSubmitting ? 'Отправляем...' : formConfig.submit_label}
        </button>

        {feedback.message ? (
          <p className={`form-feedback form-feedback-${feedback.tone}`}>{feedback.message}</p>
        ) : null}
      </form>
    </section>
  )
}

function TelegramButton({ telegramReady, telegramUrl, className = '' }) {
  if (!telegramReady) return null

  return (
    <a className={className} href={telegramUrl} rel="noreferrer" target="_blank">
      Написать в Telegram
    </a>
  )
}

function PurchaseOptionCard({ item, onBuy }) {
  return (
    <article className="purchase-card">
      <div className="purchase-copy">
        <h3>{item.title}</h3>
        <p>{item.text}</p>
      </div>

      <div className="purchase-meta">
        <p className="purchase-composition">{item.composition}</p>
        <p className="purchase-price">{item.price}</p>
        <p className="purchase-quantity">{item.quantity_note}</p>
      </div>

      <a className="primary-button purchase-button" href="#lead-form" onClick={() => onBuy(item.title)}>
        Купить
      </a>
    </article>
  )
}

function TopBar({ pageData }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useEffect(() => {
    if (!isMenuOpen) return undefined

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMenuOpen])

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth > 820) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  function toggleMenu() {
    setIsMenuOpen((current) => !current)
  }

  function closeMenu() {
    setIsMenuOpen(false)
  }

  return (
    <header className={`topbar ${isMenuOpen ? 'topbar-menu-open' : ''}`} id="top">
      <a className="brand-lockup" href="#top">
        <span className="brand-copy">
          <strong>ClusterBall</strong>
        </span>
      </a>

      <nav className="topbar-nav" aria-label="Основная навигация">
        {pageData.navigation.map((item) => (
          <a key={item.href} href={item.href}>
            {item.label}
          </a>
        ))}
      </nav>

      <div className="topbar-actions">
        <a className="topbar-cta" href="#lead-form">
          Купить
        </a>

        <button
          aria-controls="mobile-navigation"
          aria-expanded={isMenuOpen}
          aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
          className="topbar-menu-toggle"
          onClick={toggleMenu}
          type="button"
        >
          <span className="topbar-menu-toggle-icon" aria-hidden="true">
            {isMenuOpen ? <X size={18} strokeWidth={2} /> : <Menu size={18} strokeWidth={2} />}
          </span>
          <span className="topbar-menu-toggle-text">Меню</span>
        </button>
      </div>

      <div
        aria-hidden={!isMenuOpen}
        className={`topbar-mobile-menu ${isMenuOpen ? 'is-open' : ''}`}
        id="mobile-navigation"
      >
        <nav className="topbar-mobile-nav" aria-label="Мобильная навигация">
          {pageData.navigation.map((item) => (
            <a href={item.href} key={item.href} onClick={closeMenu}>
              {item.label}
            </a>
          ))}
        </nav>

        <a className="topbar-mobile-cta" href="#lead-form" onClick={closeMenu}>
          Купить
        </a>
      </div>
    </header>
  )
}

function GallerySection({ items }) {
  const [imageItem, detailItem] = items
  const imageSrc = galleryImages[imageItem?.image_key] || heroImage

  return (
    <section className="landing-section gallery-section" id="gallery">
      <div className="section-heading">
        <span className="section-kicker">Галерея</span>
        <h2>Как стол выглядит в подаче и вблизи.</h2>
      </div>

      <div className="gallery-grid">
        <article className="gallery-card gallery-card-wide">
          <div className="gallery-image-shell">
            <img
              alt={imageItem?.alt || 'Стол Кластер Болл в интерьере'}
              className="gallery-image"
              src={imageSrc}
            />
          </div>
        </article>

        <div className="gallery-copy-stack">
          {imageItem?.text ? (
            <div className="gallery-copy gallery-copy-block">
              <p>{imageItem.text}</p>
              {imageItem.text_secondary ? <p>{imageItem.text_secondary}</p> : null}
            </div>
          ) : null}

          {detailItem?.text || detailItem?.text_secondary ? (
            <div className="gallery-copy gallery-copy-block">
              {detailItem.text ? <p>{detailItem.text}</p> : null}
              {detailItem.text_secondary ? <p>{detailItem.text_secondary}</p> : null}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

function TableDetailsSection({ section }) {
  return (
    <section className="landing-section table-section" id="table-details">
      <div className="table-overview">
        <span className="section-kicker">{section.kicker}</span>
        <h2>{section.title}</h2>
        <p>{section.intro}</p>
      </div>

      <div className="table-grid">
        {section.specs.map((item) => (
          <article className="table-card" key={item.title}>
            <span className="table-card-label">{item.title}</span>
            <p>{item.text}</p>
          </article>
        ))}
      </div>

      <div className="table-emphasis">
        <p className="table-emphasis-intro">{section.outro}</p>
        <p className="table-emphasis-line">{section.emphasis}</p>
        {section.emphasis_items?.length ? (
          <ul className="table-emphasis-list">
            {section.emphasis_items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ) : null}
        <p>{section.note}</p>
      </div>
    </section>
  )
}

export default function LandingPage({ pageData }) {
  const [preselectedModel, setPreselectedModel] = useState('')

  useEffect(() => {
    const root = document.documentElement

    function syncViewportScrollbarWidth() {
      const scrollbarWidth = window.innerWidth - root.clientWidth
      root.style.setProperty('--viewport-scrollbar-width', `${Math.max(scrollbarWidth, 0)}px`)
    }

    syncViewportScrollbarWidth()
    window.addEventListener('resize', syncViewportScrollbarWidth)

    return () => {
      window.removeEventListener('resize', syncViewportScrollbarWidth)
      root.style.removeProperty('--viewport-scrollbar-width')
    }
  }, [])

  return (
    <main className="landing-shell">
      <TopBar pageData={pageData} />

      <section className="hero-section landing-section">
        <div className="hero-image-frame">
          <img alt="Стол Кластер Болл в интерьере" className="hero-image" src={heroImage} />
        </div>

        <div className="hero-overlay">
          <div className="hero-copy">
            <span className="eyebrow">{pageData.brand}</span>
            <p className="hero-kicker">{pageData.eyebrow}</p>
            <h1>{pageData.headline}</h1>
            <p className="hero-intro">{pageData.intro}</p>
            <div className="hero-actions">
              <a className="primary-button" href="#lead-form">
                Купить
              </a>
              <TelegramButton
                className="secondary-button"
                telegramReady={pageData.telegram_ready}
                telegramUrl={pageData.telegram_url}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section game-section" id="about-game">
        <div className="game-media-column">
          <div className="game-video-shell">
            <video
              aria-label="Видео с демонстрацией игры на столе ClusterBall"
              className="game-video"
              controls
              playsInline
              preload="metadata"
            >
              <source src={gameDemoVideoSrc} type="video/mp4" />
              Ваш браузер не поддерживает встроенное видео.
            </video>
          </div>
        </div>

        <div className="game-content-column">
          <div className="game-overview">
            <span className="section-kicker">Про игру</span>
            <h2>{pageData.game_overview.title}</h2>
            <p>{pageData.game_overview.text}</p>
          </div>

          <div className="game-grid">
          {pageData.game_highlights.map((item) => (
            <article className="game-card" key={item.title}>
              <h3>{item.title}</h3>
              {item.text ? <p>{item.text}</p> : null}
              {item.items?.length ? (
                <ul className="game-card-list">
                  {item.items.map((listItem) => (
                    <li key={listItem}>{listItem}</li>
                  ))}
                </ul>
              ) : null}
            </article>
          ))}
          </div>
        </div>
      </section>

      <GallerySection items={pageData.gallery} />

      <TableDetailsSection section={pageData.table_details} />

      <section className="landing-section order-section">
        <div className="section-heading">
          <span className="section-kicker">{pageData.purchase_section.kicker}</span>
          <h2>{pageData.purchase_section.title}</h2>
          <p>{pageData.purchase_section.text}</p>
        </div>

        <div className="purchase-grid">
          {pageData.purchase_options.map((item) => (
            <PurchaseOptionCard
              item={item}
              key={item.title}
              onBuy={(model) => setPreselectedModel(model)}
            />
          ))}
        </div>
      </section>

      <LeadForm
        formConfig={pageData.form}
        purchaseOptions={pageData.purchase_options}
        preselectedModel={preselectedModel}
        telegramReady={pageData.telegram_ready}
        telegramUrl={pageData.telegram_url}
      />

      <footer className="landing-footer">
        <div>
          <span className="eyebrow">{pageData.brand}</span>
          <p>{pageData.product_name}</p>
        </div>

        <nav className="footer-nav" aria-label="Навигация в подвале">
          {pageData.navigation.map((item) => (
            <a className="footer-link" href={item.href} key={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
      </footer>
    </main>
  )
}
